import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import {
  ResetTopNavigationItemsCollapsedState,
  SetActiveLeftNavigation,
  SetActiveTopNavigation,
  SetLeftNavigation,
  SetRedirectLeftNavigation,
  SetRedirectTopNavigation,
  UpdateTopNavigationItems,
} from '@core/navigation/store/actions/navigation.actions';
import { LeftNavigation } from '@core/navigation/models/left-navigation/left-navigation.type';
import { LeftNavigationItemType } from '@core/navigation/models/left-navigation/left-navigation-item-type.enum';
import { LeftNavigationExpandableItem, LeftNavigationItem } from '@core/navigation/models/left-navigation/left-navigation-item.interface';
import {
  initialTopNavigationItems,
  TopNavigationItem,
  userSettingsTopNavigationItem,
  adminSettingsTopNavigationItem,
} from '@core/navigation/models/top-navigation/top-navigation-item.interface';
import { Feature, Permission } from '@kaseya-shared';
import { Country } from '@core/locations/models/country.enum';

interface NavigationStateModel {
  activeTopNavigation: TopNavigationPath;
  topNavigationItems: TopNavigationItem[];
  settingsNavigationItems: TopNavigationItem[];
  activeLeftNavigation?: string;
  redirectLeftNavigation?: string;
  redirectTopNavigation?: TopNavigationPath;
  leftNavigation?: LeftNavigation;
}

@State<NavigationStateModel>({
  name: 'navigation',
  defaults: {
    topNavigationItems: initialTopNavigationItems,
    settingsNavigationItems: [adminSettingsTopNavigationItem, userSettingsTopNavigationItem],
    activeTopNavigation: TopNavigationPath.Home,
  },
})
@Injectable()
export class NavigationState {
  @Selector()
  static activeTopNavigation(state: NavigationStateModel): TopNavigationPath {
    return state.activeTopNavigation;
  }

  @Selector()
  static activeLeftNavigation(state: NavigationStateModel): string | undefined {
    return state.activeLeftNavigation;
  }

  @Selector()
  static redirectLeftNavigation(state: NavigationStateModel): string | undefined {
    return state.redirectLeftNavigation;
  }

  @Selector()
  static redirectTopNavigation(state: NavigationStateModel): string | undefined {
    return state.redirectTopNavigation;
  }

  @Selector()
  static nextRedirectLeftNavigation(state: NavigationStateModel): string | undefined {
    if (!state.redirectTopNavigation || !state.redirectLeftNavigation) {
      return;
    }

    const navigationItems = state.leftNavigation?.[state.redirectTopNavigation];

    if (!navigationItems) {
      return;
    }

    const currentRedirectItemIndex = navigationItems.findIndex(({ id }) => state.redirectLeftNavigation === id);

    return navigationItems[currentRedirectItemIndex + 1]?.id;
  }

  @Selector()
  static topNavigationItems(state: NavigationStateModel): TopNavigationItem[] {
    return state.topNavigationItems;
  }

  @Selector()
  static settingsNavigationItems(state: NavigationStateModel): TopNavigationItem[] {
    return state.settingsNavigationItems;
  }

  @Action(SetActiveTopNavigation)
  setActiveTopNavigation(context: StateContext<NavigationStateModel>, { navigation }: SetActiveTopNavigation): void {
    context.patchState({ activeTopNavigation: navigation });
  }

  @Action(SetActiveLeftNavigation)
  setActiveLeftNavigation(context: StateContext<NavigationStateModel>, { navigationId }: SetActiveLeftNavigation): void {
    context.patchState({ activeLeftNavigation: navigationId });
  }

  @Action(SetRedirectLeftNavigation)
  setRedirectLeftNavigation(context: StateContext<NavigationStateModel>, { navigationId }: SetRedirectLeftNavigation): void {
    context.patchState({ redirectLeftNavigation: navigationId });
  }

  @Action(SetRedirectTopNavigation)
  setRedirectTopNavigation(context: StateContext<NavigationStateModel>, { topNavigationPath }: SetRedirectTopNavigation): void {
    context.patchState({ redirectTopNavigation: topNavigationPath });
  }

  @Action(ResetTopNavigationItemsCollapsedState)
  resetTopNavigationItemsCollapsedState(context: StateContext<NavigationStateModel>): void {
    const { topNavigationItems, settingsNavigationItems } = context.getState();

    topNavigationItems.forEach(item => item.isCollapsed?.set(true));
    settingsNavigationItems.forEach(item => item.isCollapsed?.set(true));

    context.patchState({ topNavigationItems, settingsNavigationItems });
  }

  @Action(SetLeftNavigation)
  setLeftNavigationItems(context: StateContext<NavigationStateModel>, { leftNavigation }: SetLeftNavigation): void {
    context.patchState({ leftNavigation });
  }

  @Action(UpdateTopNavigationItems)
  updateTopNavigationItems(context: StateContext<NavigationStateModel>): void {
    const { leftNavigation, topNavigationItems } = context.getState();

    context.patchState({
      topNavigationItems: topNavigationItems.map(navigationItem => {
        const leftNavigationItems = leftNavigation?.[navigationItem.id];

        if (!leftNavigationItems) {
          return navigationItem;
        }

        return {
          ...navigationItem,
          permissions: this.getTopNavigationPermissions(leftNavigationItems),
          features: this.getTopNavigationAccessItems<Feature>(leftNavigationItems, 'feature'),
          accessibleCountries: this.getTopNavigationAccessItems<Country[]>(leftNavigationItems, 'accessibleCountries')?.flat(),
        };
      }),
    });
  }

  private getTopNavigationPermissions(
    leftNavigationItems: (LeftNavigationItem | LeftNavigationExpandableItem)[]
  ): Permission[] | undefined {
    const permissions = this.getTopNavigationAccessItems<Permission | Permission[]>(leftNavigationItems, 'permission');

    return permissions?.flatMap(permission => (permission instanceof Array ? permission : [permission]));
  }

  private getTopNavigationAccessItems<T>(
    leftNavigationItems: (LeftNavigationItem | LeftNavigationExpandableItem)[],
    key: keyof LeftNavigationItem
  ): T[] | undefined {
    const accessItems = leftNavigationItems
      .flatMap(leftNavigationItem => {
        if (leftNavigationItem.type !== LeftNavigationItemType.Expandable) {
          return leftNavigationItem[key] as T | undefined;
        }

        return (leftNavigationItem as LeftNavigationExpandableItem).children.map(item => item[key] as T | undefined);
      })
      .filter(value => value !== undefined);

    return accessItems.length >= this.getLeftNavigationAllItemsLength(leftNavigationItems) ? accessItems : undefined;
  }

  private getLeftNavigationAllItemsLength(leftNavigationItems: LeftNavigationItem[]): number {
    return leftNavigationItems
      .map(item => (item.type !== LeftNavigationItemType.Expandable ? item : (item as LeftNavigationExpandableItem).children))
      .flat().length;
  }
}
